import React, { useEffect, useState } from "react";
import TabsNavigation from './Tabs/TabsNavigation';
import SearchAndFilter from '../../../shared/SearchAndFilter';
import RootPools from './Tabs/RootPools';
import ChildPools from './Tabs/ChildPools';
import MyStakings from './Tabs/MyStakings';
import { useHistory } from 'react-router-dom';
import useWallet from '../../../hooks/useWallet';
import useRootPoolsAPI from '../hooks/useRootPoolsAPI';
import useChildPoolsAPI from "../hooks/useChildPoolsAPI";
import { useMyStakes } from "../hooks/useMyStakes";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import Loader from "../../../shared/Loader";
import { FARMS_SORT_OPTIONS, STAKING_SORT_OPTIONS, STAKES_SORT_OPTIONS } from "../../../utils/sortConfig";

function PoolsData({ setDummyData }) {
    const [loading, setLoading] = useState(false);
    const [loaderText, setLoaderText] = useState("");
    const { address } = useWallet();
    const history = useHistory();

    // Active tab state
    const [activeTab, setActiveTab] = useState('root-pools');

    // Root pools state
    const [rootPoolsPage, setRootPoolsPage] = useState(1);
    const [rootPoolsPerPage, setRootPoolsPerPage] = useState(10);
    const [rootPoolsSearch, setRootPoolsSearch] = useState('');
    const [rootPoolsSort, setRootPoolsSort] = useState(FARMS_SORT_OPTIONS[0]);

    // Child pools state
    const [childPoolsPage, setChildPoolsPage] = useState(1);
    const [childPoolsPerPage, setChildPoolsPerPage] = useState(10);
    const [childPoolsSearch, setChildPoolsSearch] = useState('');
    const [childPoolsSort, setChildPoolsSort] = useState(STAKING_SORT_OPTIONS[0]);

    // My stakes state
    const [myStakesPage, setMyStakesPage] = useState(1);
    const [myStakesPerPage, setMyStakesPerPage] = useState(10);
    const [myStakesSearch, setMyStakesSearch] = useState('');
    const [myStakesSort, setMyStakesSort] = useState(STAKES_SORT_OPTIONS[0]);

    // Debounced search values - these will trigger API calls
    const debouncedRootPoolsSearch = useDebouncedValue(rootPoolsSearch, 500);
    const debouncedChildPoolsSearch = useDebouncedValue(childPoolsSearch, 500);
    const debouncedMyStakesSearch = useDebouncedValue(myStakesSearch, 500);

    // Use the root pools hook
    const {
        rootPools,
        isLoading: isLoadingRootPools,
        error,
        pagination: rootPoolsPagination,
        fetchRootPools
    } = useRootPoolsAPI();

    // Use the child pools hook
    const {
        childPools,
        isLoading: isLoadingChildPools,
        error: childPoolsError,
        pagination: childPoolsPagination,
        fetchChildPools
    } = useChildPoolsAPI();

    // Use the my stakes hook with pagination, search, and sort
    const myStakesOffset = (myStakesPage - 1) * myStakesPerPage;
    const {
        data: myStakes,
        loading: isLoadingMyStakes,
        error: myStakesError,
        total: myStakesTotal,
        refetch: refetchMyStakes
    } = useMyStakes(address, myStakesPerPage, myStakesOffset, debouncedMyStakesSearch, myStakesSort.value);

    function callRootData() {
        const offset = (rootPoolsPage - 1) * rootPoolsPerPage;
        fetchRootPools(
            rootPoolsPerPage,
            offset,
            debouncedRootPoolsSearch,
            rootPoolsSort.value
        );
    }

    // Fetch root pools ONLY when root-pools tab is active
    useEffect(() => {
        if (address && activeTab === 'root-pools') {
            const offset = (rootPoolsPage - 1) * rootPoolsPerPage;
            fetchRootPools(
                rootPoolsPerPage,
                offset,
                debouncedRootPoolsSearch,
                rootPoolsSort.value
            );
        }
    }, [address, activeTab, rootPoolsPage, rootPoolsPerPage, debouncedRootPoolsSearch, rootPoolsSort, fetchRootPools]);

    // Fetch child pools ONLY when child-pools tab is active
    useEffect(() => {
        if (address && activeTab === 'child-pools') {
            const offset = (childPoolsPage - 1) * childPoolsPerPage;
            fetchChildPools(
                childPoolsPerPage,
                offset,
                debouncedChildPoolsSearch,
                childPoolsSort.value
            );
        }
    }, [address, activeTab, childPoolsPage, childPoolsPerPage, debouncedChildPoolsSearch, childPoolsSort, fetchChildPools]);

    // My stakes effect is handled by the useMyStakes hook internally
    // But we can add a condition to prevent unnecessary calls
    useEffect(() => {
        if (address && activeTab === 'my-stakes') {
            refetchMyStakes();
        }
    }, [address, activeTab]);

    // Root pools handlers
    const handleRootPoolsSearchChange = (e) => {
        setRootPoolsSearch(e.target.value);
        setRootPoolsPage(1);
    };

    const handleRootPoolsSortChange = (newSort) => {
        setRootPoolsSort(newSort);
        setRootPoolsPage(1);
    };

    const handleRootPoolsPageChange = (newPage) => {
        setRootPoolsPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleRootPoolsPerPageChange = (newPerPage) => {
        setRootPoolsPerPage(newPerPage);
        setRootPoolsPage(1);
    };

    // Child pools handlers
    const handleChildPoolsSearchChange = (e) => {
        setChildPoolsSearch(e.target.value);
        setChildPoolsPage(1);
    };

    const handleChildPoolsSortChange = (newSort) => {
        setChildPoolsSort(newSort);
        setChildPoolsPage(1);
    };

    const handleChildPoolsPageChange = (newPage) => {
        setChildPoolsPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleChildPoolsPerPageChange = (newPerPage) => {
        setChildPoolsPerPage(newPerPage);
        setChildPoolsPage(1);
    };

    // My stakes handlers
    const handleMyStakesSearchChange = (e) => {
        setMyStakesSearch(e.target.value);
        setMyStakesPage(1);
    };

    const handleMyStakesSortChange = (newSort) => {
        setMyStakesSort(newSort);
        setMyStakesPage(1);
    };

    const handleMyStakesPageChange = (newPage) => {
        setMyStakesPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleMyStakesPerPageChange = (newPerPage) => {
        setMyStakesPerPage(newPerPage);
        setMyStakesPage(1);
    };

    // Get current search and sort based on active tab
    const getCurrentSearchAndSort = () => {
        switch (activeTab) {
            case 'root-pools':
                return {
                    searchValue: rootPoolsSearch,
                    onSearchChange: handleRootPoolsSearchChange,
                    sortBy: rootPoolsSort,
                    onSortChange: handleRootPoolsSortChange,
                    sortOptions: FARMS_SORT_OPTIONS,
                    searchPlaceholder: 'Search root pools...'
                };
            case 'child-pools':
                return {
                    searchValue: childPoolsSearch,
                    onSearchChange: handleChildPoolsSearchChange,
                    sortBy: childPoolsSort,
                    onSortChange: handleChildPoolsSortChange,
                    sortOptions: STAKING_SORT_OPTIONS,
                    searchPlaceholder: 'Search child pools...'
                };
            case 'my-stakes':
                return {
                    searchValue: myStakesSearch,
                    onSearchChange: handleMyStakesSearchChange,
                    sortBy: myStakesSort,
                    onSortChange: handleMyStakesSortChange,
                    sortOptions: STAKES_SORT_OPTIONS,
                    searchPlaceholder: 'Search by contract, ID, or tx hash...'
                };
            default:
                return {
                    searchValue: rootPoolsSearch,
                    onSearchChange: handleRootPoolsSearchChange,
                    sortBy: rootPoolsSort,
                    onSortChange: handleRootPoolsSortChange,
                    sortOptions: FARMS_SORT_OPTIONS,
                    searchPlaceholder: 'Search root pools...'
                };
        }
    };

    // Show loading state - only for the active tab
    useEffect(() => {
        const currentTabLoading =
            (activeTab === 'root-pools' && isLoadingRootPools) ||
            (activeTab === 'child-pools' && isLoadingChildPools) ||
            (activeTab === 'my-stakes' && isLoadingMyStakes);

        if (currentTabLoading) {
            setLoading(true);
            const tabNames = {
                'root-pools': 'root pools',
                'child-pools': 'child pools',
                'my-stakes': 'my stakes'
            };
            setLoaderText(`Loading ${tabNames[activeTab]}...`);
        } else {
            setLoading(false);
            setLoaderText("");
        }
    }, [activeTab, isLoadingRootPools, isLoadingChildPools, isLoadingMyStakes]);

    const searchAndSortProps = getCurrentSearchAndSort();

    return (
        <>
            {loading && <Loader loadingText={loaderText} />}
            <div className="innner_topp">
                <div>
                    <div className="uppper_tabs bottomside nosideeeee">
                        <TabsNavigation
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        />
                        <SearchAndFilter {...searchAndSortProps} />
                    </div>

                    <div className="tab-content" id="pills-tabContent">
                        <RootPools
                            rootPools={rootPools}
                            currentPage={rootPoolsPage}
                            perPage={rootPoolsPerPage}
                            totalItems={rootPoolsPagination.total}
                            onPageChange={handleRootPoolsPageChange}
                            onPerPageChange={handleRootPoolsPerPageChange}
                            searchValue={rootPoolsSearch}
                            fetchRootPools={callRootData}
                        />
                        <ChildPools
                            childPools={childPools}
                            currentPage={childPoolsPage}
                            perPage={childPoolsPerPage}
                            totalItems={childPoolsPagination.total}
                            onPageChange={handleChildPoolsPageChange}
                            onPerPageChange={handleChildPoolsPerPageChange}
                            searchValue={childPoolsSearch}
                            setDummyData={setDummyData}
                        />
                        <MyStakings
                            myStakes={myStakes}
                            loading={isLoadingMyStakes}
                            error={myStakesError}
                            refetch={refetchMyStakes}
                            searchValue={myStakesSearch}
                            sortBy={myStakesSort}
                            currentPage={myStakesPage}
                            perPage={myStakesPerPage}
                            totalItems={myStakesTotal}
                            onPageChange={handleMyStakesPageChange}
                            onPerPageChange={handleMyStakesPerPageChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default PoolsData;